import React, { useEffect, useState } from "react";
import {
	Box, Typography, Select, MenuItem, Chip, IconButton, Grid,
} from "@mui/material";
import { FiDownload, FiLoader } from "react-icons/fi";
import { Helmet } from "react-helmet";
import api from "../../../services/Axios";
import AdminPageHeader, { PageSpinner } from "../../../components/AdminPageHeader";
import {
	DEFAULT_CREATOR, loadConversationUsers, useAdminData,
} from "../../../services/adminDataStore";

const CARD = {
	backgroundColor: "#FFFFFF",
	borderRadius: "20px",
	border: "1px solid #EEF0F5",
	boxShadow: "0 2px 8px rgba(17,19,26,0.04)",
};

const MODE_COLORS = { chat: "#60a5fa", voice: "#4ade80", video: "#fbbf24" };

const selectSx = {
	color: "#1F2430",
	backgroundColor: "#F7F8FC",
	minWidth: 160,
	fontSize: 13,
	borderRadius: "12px",
	"& .MuiOutlinedInput-notchedOutline": { borderColor: "#E2E4EE" },
};

function ModeBadge({ mode }) {
	if (!mode) return null;
	return (
		<Chip
			label={mode}
			size="small"
			sx={{
				fontSize: 10,
				height: 20,
				backgroundColor: `${MODE_COLORS[mode] || "#9CA3AF"}22`,
				color: MODE_COLORS[mode] || "#9CA3AF",
				textTransform: "capitalize",
			}}
		/>
	);
}

export default function Conversations() {
	const { creatorList, conversationUsersByCreator, loaded } = useAdminData();
	const creatorData = creatorList || [];
	const [selectedCreator, setSelectedCreator] = useState(DEFAULT_CREATOR);
	const [selectedUser, setSelectedUser] = useState(null);
	const [messages, setMessages] = useState([]);
	const [loadingMsgs, setLoadingMsgs] = useState(false);
	const [modeFilter, setModeFilter] = useState("all");
	const [exportingUserId, setExportingUserId] = useState(null);

	const users = conversationUsersByCreator?.[selectedCreator] || [];
	const loadingUsers = Boolean(loaded && selectedCreator && conversationUsersByCreator?.[selectedCreator] == null);

	useEffect(() => {
		if (!creatorData.length) return;
		const hasSelected = creatorData.some((c) => c?.username === selectedCreator);
		if (!hasSelected) {
			const reya = creatorData.find((c) => (c?.username || "").toLowerCase() === DEFAULT_CREATOR);
			setSelectedCreator(reya?.username || creatorData[0]?.username || "");
		}
	}, [creatorData, selectedCreator]);

	useEffect(() => {
		if (!selectedCreator) return;
		setSelectedUser(null);
		setMessages([]);
		loadConversationUsers(selectedCreator);
	}, [selectedCreator]);

	const fetchMessagesForUser = async (user) => {
		const creator = creatorData?.find((c) => c?.username === selectedCreator);
		if (!creator) return [];
		const res = await api.get(`/admin/conversations/messages?user_id=${user?.user_id}&creator_id=${creator?.user_id}`);
		return res?.data || [];
	};

	const handleSelectUser = (user) => {
		setSelectedUser(user);
		setMessages([]);
		if (!selectedCreator) return;
		setLoadingMsgs(true);
		fetchMessagesForUser(user)
			.then(setMessages)
			.finally(() => setLoadingMsgs(false));
	};

	const downloadAsText = (user, msgs) => {
		const lines = [
			`Conversation export`,
			`Creator: ${selectedCreator}`,
			`User: ${user?.name || user?.user_id}`,
			`User ID: ${user?.user_id}`,
			`Exported: ${new Date().toLocaleString()}`,
			`Messages: ${msgs.length}`,
			"".padEnd(40, "-"),
			"",
			...msgs.map((m) => {
				const time = m?.sent_at ? new Date(m.sent_at).toLocaleString() : "";
				const speaker = m.role === "user" ? (user?.name || user?.user_id) : selectedCreator;
				return `[${time}] (${m.mode}) ${speaker}: ${m.content}`;
			}),
		];
		const blob = new Blob([lines.join("\n")], { type: "text/plain" });
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		const safeUserId = (user?.user_id || "user").replace(/[^a-z0-9_-]/gi, "_");
		a.download = `conversation_${selectedCreator}_${safeUserId}.txt`;
		a.click();
		URL.revokeObjectURL(url);
	};

	const visibleMessages = modeFilter === "all" ? messages : messages.filter((m) => m.mode === modeFilter);

	const handleExportCurrent = () => {
		if (!selectedUser) return;
		downloadAsText(selectedUser, visibleMessages);
	};

	const handleQuickExport = async (e, user) => {
		e.stopPropagation();
		setExportingUserId(user.user_id);
		try {
			const msgs = user.user_id === selectedUser?.user_id ? messages : await fetchMessagesForUser(user);
			downloadAsText(user, msgs);
		} finally {
			setExportingUserId(null);
		}
	};

	const panelHeight = { xs: 360, md: "calc(100vh - 170px)" };

	return (
		<Box className="dashboard-main">
			<Helmet>
				<title>Conversations</title>
			</Helmet>
			<Box maxWidth={1600} mx="auto">
				<AdminPageHeader
					title="Conversations"
					subtitle="Browse user transcripts by creator"
					actions={
					<Select
						size="small"
						value={selectedCreator}
						onChange={(e) => setSelectedCreator(e.target.value)}
						displayEmpty
						sx={selectSx}
					>
						<MenuItem value="" disabled>Select creator</MenuItem>
						{creatorData?.map((c) => (
							<MenuItem key={c?.username} value={c?.username}>{c?.username}</MenuItem>
						))}
					</Select>
					}
				/>
				{!loaded ? <PageSpinner /> : (

				<Box p={{ xs: 1.5, sm: 2.5 }} sx={CARD}>
					<Grid container spacing={2}>
						<Grid item xs={12} md={4}>
							<Box
								sx={{
									border: "1px solid #EEF0F5",
									borderRadius: "14px",
									height: panelHeight,
									overflowY: "auto",
									backgroundColor: "#FAFBFE",
								}}
							>
								{!selectedCreator && (
									<Box p={3}><Typography fontSize={13} color="#9CA3AF">Select a creator to see users.</Typography></Box>
								)}
								{loadingUsers && (
									<Box p={3}><Typography fontSize={13} color="#9CA3AF">Loading users…</Typography></Box>
								)}
								{!loadingUsers && users.map((user) => (
									<Box
										key={user.user_id}
										onClick={() => handleSelectUser(user)}
										sx={{
											p: 1.75,
											cursor: "pointer",
											borderBottom: "1px solid #EEF0F5",
											backgroundColor: selectedUser?.user_id === user.user_id ? "#FFFFFF" : "transparent",
											boxShadow: selectedUser?.user_id === user.user_id ? "inset 3px 0 0 #A855F7" : "none",
											display: "flex",
											justifyContent: "space-between",
											alignItems: "flex-start",
											"&:hover": { backgroundColor: "#FFFFFF" },
											"&:hover .export-btn": { opacity: 1 },
										}}
									>
										<Box minWidth={0} pr={1}>
											<Typography fontSize={13} color="#1F2430" fontWeight={600} noWrap>
												{user.name || "(no name)"}
											</Typography>
											<Typography fontSize={11} color="#8B93A1" mt={0.25} noWrap>{user.user_id}</Typography>
											<Box display="flex" gap={1.5} mt={0.5} flexWrap="wrap">
												<Typography fontSize={10} color="#9CA3AF">
													Msgs: <Typography component="span" fontSize={10} color="#60a5fa">{user.total_messages}</Typography>
												</Typography>
												{user.voice_min > 0 && (
													<Typography fontSize={10} color="#9CA3AF">
														Voice: <Typography component="span" fontSize={10} color="#16A34A">{user.voice_min.toFixed(1)}m</Typography>
													</Typography>
												)}
												{user.video_min > 0 && (
													<Typography fontSize={10} color="#9CA3AF">
														Video: <Typography component="span" fontSize={10} color="#d97706">{user.video_min.toFixed(1)}m</Typography>
													</Typography>
												)}
											</Box>
										</Box>
										<IconButton
											className="export-btn"
											size="small"
											onClick={(e) => handleQuickExport(e, user)}
											disabled={exportingUserId === user.user_id}
											sx={{ color: "#6B7280", opacity: 0, transition: "opacity 0.15s", "&:hover": { color: "#16A34A" } }}
											title="Export this conversation"
										>
											{exportingUserId === user.user_id ? <FiLoader size={13} /> : <FiDownload size={13} />}
										</IconButton>
									</Box>
								))}
								{!loadingUsers && selectedCreator && users.length === 0 && (
									<Box p={3}><Typography fontSize={13} color="#9CA3AF">No users found.</Typography></Box>
								)}
							</Box>
						</Grid>

						<Grid item xs={12} md={8}>
							<Box
								sx={{
									border: "1px solid #EEF0F5",
									borderRadius: "14px",
									p: 2,
									height: panelHeight,
									display: "flex",
									flexDirection: "column",
									backgroundColor: "#FFFFFF",
								}}
							>
								{!selectedUser ? (
									<Box flex={1} display="flex" alignItems="center" justifyContent="center">
										<Typography fontSize={13} color="#9CA3AF">Select a user to view conversation.</Typography>
									</Box>
								) : (
									<>
										<Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5} flexWrap="wrap" gap={1}>
											<Typography fontSize={13} color="#1F2430" fontWeight={600}>
												{selectedUser.name || selectedUser.user_id}
												<Typography component="span" fontSize={12} color="#8B93A1" fontWeight={400}>
													{" "}· {selectedCreator}
												</Typography>
											</Typography>
											<Box display="flex" alignItems="center" gap={1}>
												<Select
													size="small"
													value={modeFilter}
													onChange={(e) => setModeFilter(e.target.value)}
													sx={{ ...selectSx, minWidth: 110, height: 32, fontSize: 12 }}
												>
													<MenuItem value="all">All modes</MenuItem>
													<MenuItem value="chat">Chat</MenuItem>
													<MenuItem value="voice">Voice</MenuItem>
													<MenuItem value="video">Video</MenuItem>
												</Select>
												<Typography fontSize={11} color="#9CA3AF">{messages.length} messages</Typography>
												<IconButton
													size="small"
													onClick={handleExportCurrent}
													disabled={loadingMsgs || messages.length === 0}
													sx={{ color: "#6B7280", border: "1px solid #E2E4EE", borderRadius: 1, p: 0.5, "&:hover": { color: "#16A34A" } }}
													title="Export this conversation as .txt"
												>
													<FiDownload size={13} />
												</IconButton>
											</Box>
										</Box>
										<Box sx={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 1, pr: 0.5 }}>
											{loadingMsgs && (
												<Typography fontSize={13} color="#9CA3AF">Loading…</Typography>
											)}
											{!loadingMsgs && visibleMessages.map((m, i) => (
												<Box
													key={i}
													sx={{
														alignSelf: m.role === "user" ? "flex-end" : "flex-start",
														maxWidth: "75%",
														backgroundColor: m.role === "user" ? "#EFF6FF" : "#F7F8FC",
														borderRadius: "14px",
														p: 1.25,
													}}
												>
													<Typography fontSize={13} color="#1F2430">{m.content}</Typography>
													<Box display="flex" gap={1} mt={0.5} alignItems="center">
														<ModeBadge mode={m.mode} />
														<Typography fontSize={10} color="#8B93A1">
															{new Date(m.sent_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
														</Typography>
													</Box>
												</Box>
											))}
											{!loadingMsgs && visibleMessages.length === 0 && (
												<Typography fontSize={13} color="#8B93A1">No messages for this filter.</Typography>
											)}
										</Box>
									</>
								)}
							</Box>
						</Grid>
					</Grid>
				</Box>
				)}
			</Box>
		</Box>
	);
}
